import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import { FormattedMessage } from 'react-intl'
import { useForm } from 'react-hook-form'
import { Alert, Button, Dialog, DialogContent, DialogFooter, DialogHeader } from '@carto/meridian-ds/components'
import { useMediaQuery, useTheme } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useAppSelector, useAppDispatch } from 'app/state'
import { RequestStatus } from 'features/common'
import { App, AppTypes } from 'features/apps/types'
import { saveAppRequest } from 'features/apps/state/appsSlice'
import AppsFormCommonSectionFields from 'features/apps/ui/AppForm/AppFormCommonSection/AppsFormCommonSectionFields'

const SUCCESS_OR_ERROR_MESSAGE_VISIBILITY_TIME = 3000

interface AccountAppsRegisteredFormDialogProps {
  app?: App
  onSuccess?: { (): void }
  disabled?: boolean
}

export interface AccountAppsRegisteredFormDialogRefProps {
  show: () => void
}

const AccountAppsRegisteredFormDialog: React.ForwardRefRenderFunction<
  AccountAppsRegisteredFormDialogRefProps,
  AccountAppsRegisteredFormDialogProps
> = (props, forwardedRef) => {
  const { app, disabled, onSuccess } = props

  const dispatch = useAppDispatch()

  const saveAppError = useAppSelector((state) => state.apps.saveAppError)
  const saveAppStatus = useAppSelector((state) => state.apps.saveAppStatus)

  const [stateApp, setStateApp] = useState<App | undefined>()
  const [open, setOpen] = useState(false)

  const [displaySuccessMessage] = useState(false)
  const [displayErrorMessage, setErrorMessage] = useState(false)

  const { handleSubmit, formState, ...form } = useForm({ mode: 'onSubmit' })

  const theme = useTheme()
  const isXs = useMediaQuery(theme.breakpoints.down('sm'))

  useImperativeHandle(forwardedRef, () => ({
    show: () => {
      setOpen(true)
    }
  }))

  useEffect(() => {
    if (app) {
      setStateApp(app)
    }
  }, [app])

  useEffect(() => {
    if (saveAppStatus === RequestStatus.Fulfilled) {
      onSuccess && onSuccess()
      setOpen(false)
    }
  }, [saveAppStatus, onSuccess])

  useEffect(() => {
    if (saveAppStatus === RequestStatus.Rejected) {
      setErrorMessage(true)
    }
  }, [saveAppStatus])

  useEffect(() => {
    let timeoutId

    if (displayErrorMessage) {
      timeoutId = setTimeout(() => {
        setErrorMessage(false)
      }, SUCCESS_OR_ERROR_MESSAGE_VISIBILITY_TIME)
    }

    return () => {
      clearTimeout(timeoutId)
    }
  }, [displayErrorMessage])

  function handleOpen() {
    setOpen(true)
  }

  function handleClose() {
    setOpen(false)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSubmit = (data: any) => {
    const payload = { ...data, type: AppTypes.Listed, id: app?.id }
    dispatch(saveAppRequest(payload))
  }

  return (
    <div>
      {!app && (
        <Button onClick={handleOpen} variant="contained" disabled={disabled} color="primary" startIcon={<AddIcon />}>
          <FormattedMessage id={isXs ? 'account.cards.apps.registerNewShort' : 'account.cards.apps.registerNew'} />
        </Button>
      )}

      <Dialog
        size="medium"
        aria-label="registered-apps-form"
        open={open}
        onClose={handleClose}
        aria-labelledby="registered-apps-form-dialog-title"
      >
        <DialogHeader
          onClose={handleClose}
          id="registered-apps-form-dialog-title"
          title={
            app ? (
              <FormattedMessage id="account.cards.apps.registerAppDialog.edit" />
            ) : (
              <FormattedMessage id="account.cards.apps.registerAppDialog.title" />
            )
          }
        />
        <DialogContent>
          <div>
            <AppsFormCommonSectionFields registered={true} form={{ ...form, formState }} app={stateApp} />
          </div>
          {saveAppError && displayErrorMessage && <Alert severity="error">{saveAppError}</Alert>}
        </DialogContent>
        <DialogFooter>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            disabled={displaySuccessMessage}
            loading={saveAppStatus === RequestStatus.Pending}
          >
            <FormattedMessage
              id={app ? 'account.cards.apps.registerAppDialog.save' : 'account.cards.apps.registerAppDialog.create'}
            />
          </Button>
        </DialogFooter>
      </Dialog>
    </div>
  )
}

export default forwardRef(AccountAppsRegisteredFormDialog)
